import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Check } from "lucide-react";
import { PageHeader } from "../components/Shared";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Feedback({ guest, navigate, lang }) {
  const T = translations[lang];
  const fullName = `${guest.title} ${guest.name}`;
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const submit = async () => {
    if (rating === 0 || sending) return;
    setSending(true);
    setError(false);
    try {
      const res = await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guest_name: fullName, room_number: guest.room, rating, comment }),
      });
      if (!res.ok) throw new Error(`Feedback submit failed: ${res.status}`);
      setSubmitted(true);
      setTimeout(() => navigate("dashboard"), 3000);
    } catch (e) {
      setError(true);
      setSending(false);
    }
  };

  const display = hover || rating;

  return (
    <div className="min-h-screen" data-testid="feedback-screen">
      <PageHeader title={T.fbTitle} onBack={() => navigate("dashboard")} />

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center text-center px-8 pt-24"
            data-testid="feedback-success"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 12 }}
              className="w-20 h-20 rounded-full bg-[#2EAF7D] flex items-center justify-center mb-5"
            >
              <Check size={44} className="text-white" strokeWidth={3} />
            </motion.div>
            <h2 className="font-playfair font-semibold text-[22px] text-[#1A1A2E]">{T.fbThanksTitle}</h2>
            <p className="text-[15px] text-[#6B7B8D] mt-2 leading-relaxed">{T.fbThanksMsg(fullName)}</p>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-6 pt-8 pb-[110px] flex flex-col items-center text-center"
          >
            <div className="text-[56px] leading-none mb-2">
              {display >= 5 ? "🤩" : display >= 4 ? "😊" : display >= 3 ? "🙂" : display >= 1 ? "😕" : "😊"}
            </div>
            <h2 className="font-inter font-semibold text-[18px] text-[#1A1A2E] leading-snug max-w-[280px]">{T.fbQuestion}</h2>

            <div className="flex gap-2 mt-6">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  data-testid={`star-${s}`}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  className="active:scale-90 transition-transform p-1"
                >
                  <Star size={40} className={s <= display ? "text-[#C9A84C]" : "text-[#D9E2EC]"} fill={s <= display ? "#C9A84C" : "none"} />
                </button>
              ))}
            </div>

            <div className="h-6 mt-3">
              {rating > 0 && (
                <motion.p key={rating} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="text-[15px] font-medium text-[#1A1A2E]">
                  {T.ratingLabels[rating]}
                </motion.p>
              )}
            </div>

            <div className="w-full mt-5 relative">
              <textarea
                data-testid="feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                placeholder={T.fbPlaceholder}
                rows={4}
                className="w-full bg-white border border-[#E0E8F0] rounded-[12px] p-4 text-[15px] text-[#1A1A2E] placeholder-[#6B7B8D] resize-none focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/20 transition-all"
              />
              <span className="absolute bottom-3 right-3 text-[11px] text-[#A9B8C6]">{comment.length}/500</span>
            </div>

            {error && (
              <p data-testid="feedback-error" className="text-[13px] text-[#D64545] mt-3">
                {T.fbError}
              </p>
            )}

            <button
              data-testid="submit-feedback"
              onClick={submit}
              disabled={rating === 0 || sending}
              style={{ opacity: rating === 0 ? 0.5 : 1 }}
              className="w-full h-[52px] mt-6 rounded-[12px] bg-gradient-to-r from-[#4AADDF] to-[#367ABD] text-white font-bold text-[16px] shadow-[0_6px_18px_rgba(74,173,223,0.3)] active:scale-[0.98] transition-transform disabled:cursor-not-allowed"
            >
              {sending ? T.fbSending : T.fbSubmit}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
